// ProjectList.tsx
import { useState, useMemo } from 'react';
import { FileSpreadsheet, Trash2, LogOut, Edit, Plus, FolderOpen, ChevronDown, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import ProvafiLogo from '../../assets/Provafi Step 3 (500 x 150 px) (2)-cropped.svg';
import { Project } from '../../types';
import { authApi, projectApi } from '../../lib/api/authApi';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../ui/alert-dialog';
import { Button } from '../ui/button';

interface ProjectListProps {
  projects: Project[];
  onSelect: (project: Project) => void;
  onDelete: (projectId: string) => void;
  onLogout?: () => void;
  onNewProject?: () => void;
  onRename?: (projectId: string, newName: string) => void;
  isLoading?: boolean;
  hasInitialized?: boolean;
}

type SortField = 'name' | 'updated_at';
type SortOrder = 'asc' | 'desc';

export function ProjectList({ 
  projects, 
  onSelect, 
  onDelete, 
  onLogout, 
  onNewProject,
  onRename,
  isLoading = false,
  hasInitialized = false
}: ProjectListProps) {
  const [editingProjectId, setEditingProjectId] = useState<string | null>(null);
  const [newProjectName, setNewProjectName] = useState('');
  const [isRenaming, setIsRenaming] = useState(false);
  const [sortField, setSortField] = useState<SortField>('updated_at');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');

  const handleLogout = async () => {
    try {
      // Note: For logout, we'll need to handle this through the auth system
      // For now, just call the onLogout callback
      if (onLogout) {
        onLogout();
      }
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const handleEditClick = (project: Project) => {
    setEditingProjectId(project.id);
    setNewProjectName(project.name);
  };

  const handleRenameSubmit = async (projectId: string) => {
    if (!newProjectName.trim()) {
      return;
    }

    setIsRenaming(true);
    try {
      await projectApi.update(projectId, { 
        name: newProjectName.trim(),
        updated_at: new Date().toISOString()
      });

      // Call the onRename callback if provided
      if (onRename) {
        onRename(projectId, newProjectName.trim());
      }
    } catch (error) {
      console.error('Error renaming project:', error);
      alert('Failed to rename project. Please try again.');
    } finally {
      setEditingProjectId(null);
      setNewProjectName('');
      setIsRenaming(false);
    }
  };

  const handleCancelRename = () => {
    setEditingProjectId(null);
    setNewProjectName('');
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder(field === 'name' ? 'asc' : 'desc');
    }
  };

  const sortedProjects = useMemo(() => {
    return [...projects].sort((a, b) => {
      let aValue: string | Date;
      let bValue: string | Date;
      
      if (sortField === 'name') {
        aValue = a.name.toLowerCase();
        bValue = b.name.toLowerCase();
      } else {
        aValue = new Date(a.updated_at);
        bValue = new Date(b.updated_at);
      }
      
      if (sortOrder === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });
  }, [projects, sortField, sortOrder]);

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <ArrowUpDown className="h-4 w-4" />;
    }
    return sortOrder === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />;
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Left Navigation */}
      <div className="w-64 bg-[#1a237e] text-white h-screen flex-shrink-0 fixed left-0 top-0 flex flex-col">
        <div className="p-4 overflow-y-auto flex-1">
          <div className="flex items-center mb-8">
            {/* Logo */}
            <img 
              src={ProvafiLogo} 
              alt="Provafi" 
              className="h-8 w-auto mr-2"
            />
          </div>
          
          {/* Navigation Section */}
          <div className="mb-8">
            <h2 className="text-xs font-semibold uppercase tracking-wider mb-4 text-blue-200">
              Dashboard
            </h2>
            <nav className="space-y-1">
              <button className="w-full flex items-center px-3 py-2 text-sm rounded-md bg-blue-900 text-white">
                <FolderOpen className="h-4 w-4 mr-3" />
                Projects
              </button>
            </nav>
          </div>
        </div>
      </div>
      
      {/* Main Content Area - Remove top bar since App.tsx provides it */}
      <div className="flex-1 ml-64">
        {/* Welcome Message and Projects Content */}
        <div className="p-6">
          {/* Welcome Message */}
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 tracking-tight">AI-QoE Insights Without the Wait</h2>
            <p className="mt-6 text-xl text-gray-600 max-w-2xl mx-auto">Speeding up your financial due diligence process</p>
          </div>
          
          {/* Projects List Container */}
          <div className="max-w-4xl mx-auto">
            <div className="bg-white shadow overflow-hidden sm:rounded-md">
              {/* Sorting Controls */}
              {projects.length > 0 && (
                <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-medium text-gray-700">
                      {projects.length} project{projects.length !== 1 ? 's' : ''}
                    </div>
                    <div className="flex items-center space-x-4">
                      <span className="text-sm text-gray-600">Sort by:</span>
                      <button
                        onClick={() => handleSort('name')}
                        className={`flex items-center space-x-1 px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                          sortField === 'name' 
                            ? 'bg-blue-100 text-blue-700' 
                            : 'text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        <span>Name</span>
                        {getSortIcon('name')}
                      </button>
                      <button
                        onClick={() => handleSort('updated_at')}
                        className={`flex items-center space-x-1 px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                          sortField === 'updated_at' 
                            ? 'bg-blue-100 text-blue-700' 
                            : 'text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        <span>Last Updated</span>
                        {getSortIcon('updated_at')}
                      </button>
                    </div>
                  </div>
                </div>
              )}
      
      {(() => {
        // Show loading spinner if still loading OR not initialized
        if (isLoading || !hasInitialized) {
          return (
            <div className="p-8 flex justify-center items-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-3 text-gray-600">Loading projects...</span>
            </div>
          );
        }
        
        // Only show welcome banner for truly new users with no projects
        // All conditions must be met: not loading, initialized, and confirmed zero projects
        if (projects.length === 0 && hasInitialized && !isLoading) {
          return (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
              <h3 className="text-lg font-medium text-blue-800 mb-2">Welcome to Provafi!</h3>
              <p className="text-blue-700 mb-4">You don't have any projects yet. Click the "New Project" button at the top to get started.</p>
            </div>
          );
        }
        
        return (
          <ul className="divide-y divide-gray-200">
            {sortedProjects.filter(project => project.id).map((project) => (
              <li key={project.id}>
                <div className="px-4 py-4 flex items-center justify-between sm:px-6">
                  <div className="flex items-center flex-grow mr-4">
                    <FileSpreadsheet className="h-5 w-5 text-gray-400 mr-3" />
                    {editingProjectId === project.id ? (
                      <div className="flex items-center flex-grow">
                        <input
                          type="text"
                          value={newProjectName}
                          onChange={(e) => setNewProjectName(e.target.value)}
                          className="flex-grow pl-3 pr-2 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-1 focus:ring-blue-400 focus:border-blue-400"
                          autoFocus
                          placeholder="Enter project name"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              handleRenameSubmit(project.id);
                            } else if (e.key === 'Escape') {
                              handleCancelRename();
                            }
                          }}
                        />
                        <div className="flex ml-2 space-x-2">
                          <Button 
                            onClick={() => handleRenameSubmit(project.id)} 
                            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2"
                            disabled={isRenaming}
                          >
                            {isRenaming ? 'Saving...' : 'Save'}
                          </Button>
                          <Button 
                            onClick={handleCancelRename}
                            className="px-6 py-2"
                            variant="outline"
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <h3 className="text-sm font-medium text-gray-900">{project.name}</h3>
                        <p className="text-sm text-gray-500">
                          Last updated: {new Date(project.updated_at).toLocaleDateString()}
                        </p>
                      </div>
                    )}
                  </div>
                  {editingProjectId !== project.id && (
                    <div className="flex space-x-2">
                      <Button
                        onClick={() => onSelect(project)}
                        className="bg-blue-600 px-4 py-2 hover:bg-blue-700 font-medium rounded-md text-white "
                      >
                        Open
                      </Button>
                      <Button
                        onClick={() => handleEditClick(project)}
                        variant="outline"
                        className="px-2"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="outline"
                            className="text-red-600 hover:text-red-700 px-2"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Project</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to delete {project.name}? This action cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel
                              className="px-6 py-2"
                            >
                              Cancel
                            </AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => onDelete(project.id)}
                              className="bg-red-600 hover:bg-red-700 text-white px-6 py-2"
                            >
                              Delete Project
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        );
      })()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
